/* public/js/ielts-scorer/analyze.js — the instant, deterministic layer of /ielts-scorer.
 *
 * Pure functions of the text: no network, no model, no cost. This is what a
 * candidate sees the moment they type — before, and without, the examiner grade.
 * Every finding names the criterion it touches (task · coherence · lexical ·
 * grammar) and the exact spans in the text, so the page can highlight them;
 * the wording lives in the page (10 locales), not here.
 *
 * What it deliberately does NOT do: guess a band. A band from surface counts is
 * the inflated number this tool exists to replace. It reports only what is
 * mechanically true (word count, paragraphs, a misspelt word, a contraction) and
 * the few descriptor rules that follow from them (no paragraphs → the Band 5
 * coherence descriptor; no overview in Academic Task 1 → the Band 5 Task
 * Achievement descriptor). Thresholds were set on 1 435 examiner-commented
 * essays (see tests/js/ielts-analyze.test.mjs and the tool memory).
 *
 *   IeltsAnalyze.analyze(text, {task, question, dict}) → report
 *   IeltsAnalyze.wordCount(text) — the SAME count as App\Services\Ielts\BandMath
 *
 * `dict` (optional) is window.LangFreq once its word list has loaded: it adds
 * the spelling check and the vocabulary profile. Without it everything else
 * still runs.
 *
 * window.IeltsAnalyze + module.exports (node --test).
 */
(function (root, factory) {
    'use strict';
    var A = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = A;
    if (typeof window !== 'undefined') window.IeltsAnalyze = A;
    else if (root) root.IeltsAnalyze = A;
})(typeof self !== 'undefined' ? self : this, function () {
'use strict';

var MIN_WORDS = { task1: 150, task2: 250 };

/* Every map keyed by a WORD has no prototype: «constructor» is a word too, and
   on a plain object it is a function — the essay that used it crashed the check. */
function bag(o) { var b = Object.create(null); if (o) for (var k in o) b[k] = o[k]; return b; }

function family(task) { return String(task || '').indexOf('task1') === 0 ? 'task1' : 'task2'; }

/* Words the way the examiner's counter counts them: whitespace-delimited
   tokens. Must stay identical to BandMath::wordCount() — the page's
   «187 / 250» and the server's under-length flag are one number. */
function wordCount(text) {
    // \s in JS also matches U+FEFF; PHP's /\s/u (Unicode properties) does not.
    var t = String(text || '').replace(/[^\S\uFEFF]+/g, ' ').replace(/^ +| +$/g, '');
    return t ? t.split(' ').length : 0;
}

/* ── tokens with offsets ─────────────────────────────────────────────────── */
var WORD_RE = /[A-Za-z][A-Za-z'’]*(?:-[A-Za-z][A-Za-z'’]*)*/g;

function tokens(text) {
    var out = [], m;
    WORD_RE.lastIndex = 0;
    while ((m = WORD_RE.exec(text))) {
        var w = m[0].replace(/['’]+$/, '');
        out.push({ w: w, lc: w.toLowerCase().replace(/’/g, "'"), s: m.index, e: m.index + w.length });
    }
    return out;
}

/* ── paragraphs ──────────────────────────────────────────────────────────────
   A line break is a paragraph break. People type essays in a textarea and
   press Enter once between paragraphs far more often than twice; the computer-
   delivered test shows them the same way. Counting only blank lines told a
   four-paragraph essay it was «one block». */
function paragraphs(text) {
    var out = [], re = /[^\n]+/g, m;
    while ((m = re.exec(text))) {
        var raw = m[0];
        if (!raw.trim()) continue;
        var lead = raw.length - raw.replace(/^\s+/, '').length;
        var body = raw.trim();
        out.push({ s: m.index + lead, e: m.index + lead + body.length, text: body });
    }
    return out;
}

/* ── sentences ───────────────────────────────────────────────────────────── */
var ABBR = /(?:^|[\s(])(?:e\.g|i\.e|etc|vs|mr|mrs|ms|dr|prof|st|no|approx|cf|al|inc|ltd|jr|sr|u\.s|u\.k|a\.m|p\.m|[A-Z])$/i;

function sentences(text) {
    var out = [], start = -1, i, n = text.length;
    function close(end) {
        if (start < 0) return;
        var seg = text.slice(start, end), lead = seg.length - seg.replace(/^\s+/, '').length;
        var body = seg.trim();
        if (body) out.push({ s: start + lead, e: start + lead + body.length, text: body });
        start = -1;
    }
    for (i = 0; i < n; i++) {
        var c = text[i];
        if (start < 0) { if (!/\s/.test(c)) start = i; continue; }
        if (c === '\n') { close(i); continue; }
        if (c === '.' || c === '!' || c === '?') {
            var j = i;
            while (j + 1 < n && /[.!?]/.test(text[j + 1])) j++;
            while (j + 1 < n && /["'”’)\]]/.test(text[j + 1])) j++;
            var next = text[j + 1];
            if (next !== undefined && !/\s/.test(next)) { i = j; continue; }   // 3.5, e.g.x, URL
            if (c === '.' && ABBR.test(text.slice(Math.max(0, i - 6), i))) { i = j; continue; }
            close(j + 1);
            i = j;
        }
    }
    close(n);
    return out;
}

/* ── linking devices ─────────────────────────────────────────────────────── */
var LINKERS = {
    add: ['moreover', 'furthermore', 'in addition', 'additionally', 'besides', 'what is more', 'as well as', 'not only'],
    contrast: ['however', 'nevertheless', 'nonetheless', 'on the other hand', 'in contrast', 'by contrast', 'conversely', 'although', 'even though', 'whereas', 'despite', 'in spite of', 'on the contrary', 'while'],
    cause: ['therefore', 'thus', 'hence', 'consequently', 'as a result', 'as a consequence', 'accordingly', 'for this reason', 'due to', 'owing to', 'because of', 'since'],
    example: ['for example', 'for instance', 'such as', 'namely', 'to illustrate', 'in particular'],
    sequence: ['firstly', 'first of all', 'secondly', 'thirdly', 'finally', 'lastly', 'to begin with', 'subsequently', 'meanwhile'],
    conclude: ['in conclusion', 'to conclude', 'to sum up', 'in summary', 'all in all', 'in short', 'overall'],
    emphasis: ['indeed', 'in fact', 'undoubtedly', 'notably', 'clearly']
};
var LINKER_LIST = [];
(function () {
    for (var k in LINKERS) for (var i = 0; i < LINKERS[k].length; i++) LINKER_LIST.push({ p: LINKERS[k][i], k: k });
    LINKER_LIST.sort(function (a, b) { return b.p.length - a.p.length; });   // longest first: «on the other hand» before «on»
})();
/* Only these get the «said three times» warning: a sentence adverb repeated is
   what examiners call mechanical. «such as», «because of», «while» are grammar,
   not signposting, and repeating them is normal. */
var SIGNPOSTS = bag({ moreover: 1, furthermore: 1, 'in addition': 1, additionally: 1, however: 1, nevertheless: 1, nonetheless: 1,
    'on the other hand': 1, therefore: 1, thus: 1, hence: 1, consequently: 1, 'as a result': 1, 'for example': 1,
    'for instance': 1, firstly: 1, secondly: 1, finally: 1, 'in conclusion': 1, indeed: 1, 'in fact': 1, besides: 1, 'what is more': 1 });

function escRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
function phraseRe(p) { return new RegExp('\\b' + escRe(p).replace(/ /g, '\\s+').replace(/'/g, "['’]") + '\\b', 'gi'); }

function findLinkers(text) {
    var taken = [], hits = [];
    for (var i = 0; i < LINKER_LIST.length; i++) {
        var L = LINKER_LIST[i], re = phraseRe(L.p), m;
        while ((m = re.exec(text))) {
            var s = m.index, e = s + m[0].length, clash = false;
            for (var t = 0; t < taken.length; t++) if (s < taken[t][1] && e > taken[t][0]) { clash = true; break; }
            if (clash) continue;
            taken.push([s, e]);
            hits.push({ p: L.p, k: L.k, s: s, e: e });
        }
    }
    hits.sort(function (a, b) { return a.s - b.s; });
    return hits;
}

/* ── word lists ──────────────────────────────────────────────────────────── */
var STOP = bag();
('the a an and or but of to in on for with as at by from is are was were be been being it its this that these those '
 + 'you he she they we my your their our his her them not no do does did so if then than too very can will would should '
 + 'could may might must have has had also which who whom whose what when where why how there here about into over more '
 + 'most some such only because while however therefore thus other others many much each every all both any one ones own '
 + 'same just even also well still yet again ever never always often like make made get got take given one two three '
 + 'first second new way ways part parts way things thing lot lots kind sort time times year years day days').split(' ')
    .forEach(function (w) { STOP[w] = 1; });

/* Instruction boilerplate of an IELTS prompt — not the topic. Without this,
   «to what extent do you agree» made «extent» a topic word for every essay. */
var PROMPT_BOILER = bag();
('agree disagree extent discuss views opinion give reasons answer include relevant examples knowledge experience '
 + 'write least words essay letter chart graph table diagram below shows show summarise summarize information selecting '
 + 'reporting main features make comparisons where comparisons advantages disadvantages outweigh positive negative '
 + 'development causes problem problems solutions solve measures taken think believe people some others other '
 + 'should would could whether question questions view both these dear begin follows need addresses top yours').split(' ')
    .forEach(function (w) { PROMPT_BOILER[w] = 1; });

var CONTRACTIONS = bag({
    "don't": 'do not', "doesn't": 'does not', "didn't": 'did not', "can't": 'cannot', "couldn't": 'could not',
    "won't": 'will not', "wouldn't": 'would not', "shouldn't": 'should not', "isn't": 'is not', "aren't": 'are not',
    "wasn't": 'was not', "weren't": 'were not', "hasn't": 'has not', "haven't": 'have not', "hadn't": 'had not',
    "mustn't": 'must not', "needn't": 'need not', "it's": 'it is', "that's": 'that is', "there's": 'there is',
    "what's": 'what is', "who's": 'who is', "let's": 'let us', "i'm": 'I am', "you're": 'you are', "we're": 'we are',
    "they're": 'they are', "i've": 'I have', "you've": 'you have', "we've": 'we have', "they've": 'they have',
    "i'll": 'I will', "you'll": 'you will', "we'll": 'we will', "they'll": 'they will', "it'll": 'it will',
    "i'd": 'I would', "you'd": 'you would', "we'd": 'we would', "they'd": 'they would', "he's": 'he is', "she's": 'she is'
});

/* Informal words in a formal answer. `to` is a one-click replacement only when
   it is safe in every context; otherwise `alt` is shown as a suggestion. */
var INFORMAL = [
    { p: 'a lot of', alt: 'many · much · a great deal of' },
    { p: 'lots of', alt: 'many · numerous · a large number of' },
    { p: 'loads of', alt: 'many · a large number of' },
    { p: 'tons of', alt: 'many · a large number of' },
    { p: 'kids', to: 'children' },
    { p: 'stuff', alt: 'things · items · material' },
    { p: 'guys', alt: 'people' },
    { p: 'gonna', to: 'going to' },
    { p: 'wanna', to: 'want to' },
    { p: 'gotta', to: 'have to' },
    { p: 'okay', alt: 'acceptable · satisfactory' },
    { p: 'ok', alt: 'acceptable · satisfactory' },
    { p: 'awesome', alt: 'excellent · impressive' },
    { p: 'cool', alt: 'excellent · appealing' },
    { p: 'pretty much', alt: 'almost · largely' },
    { p: 'pretty good', alt: 'fairly good · quite good' },
    { p: 'totally', alt: 'completely · entirely' },
    { p: 'nowadays a lot', alt: 'today many' },
    { p: 'super', alt: 'very · extremely' }
];

/* Memorised phrases. Examiners are trained to discount formulaic language — it
   shows a learnt chunk, not lexical resource — and these are the ones that
   recur in thousands of scripts. */
var CLICHES = [
    'in this day and age', "in today's modern world", "in today's world", 'since time immemorial',
    'every coin has two sides', 'a coin has two sides', 'two sides of the same coin', 'it is a well-known fact',
    'it is a well known fact', 'it goes without saying', 'last but not least', 'first and foremost', 'in a nutshell',
    'a double-edged sword', 'a double edged sword', 'has its own pros and cons', 'the pros and cons',
    'there are two sides to every story', 'a hot topic', 'a burning issue', 'has sparked a heated debate',
    'from time immemorial', 'the advent of technology', 'plays a pivotal role'
];

var VAGUE = bag({ thing: 'factor · aspect · issue', things: 'factors · aspects · issues', good: 'beneficial · valuable · effective',
    bad: 'harmful · detrimental · damaging', big: 'considerable · substantial · significant', nice: 'pleasant · appealing',
    get: 'obtain · receive · become', got: 'obtained · received · became' });

/* Well-known misspellings that sit in a subtitle-frequency list anyway
   («untill», «wich» appear in subtitles), so the dictionary alone misses them. */
var MISSPELT = bag({
    untill: 'until', wich: 'which', alot: 'a lot', thier: 'their', recieve: 'receive', recieved: 'received',
    beleive: 'believe', belive: 'believe', definately: 'definitely', definatly: 'definitely', occured: 'occurred',
    occurence: 'occurrence', seperate: 'separate', goverment: 'government', enviroment: 'environment',
    becuase: 'because', beacuse: 'because', tommorow: 'tomorrow', accomodation: 'accommodation',
    neccessary: 'necessary', necesary: 'necessary', sucessful: 'successful', succesful: 'successful',
    begining: 'beginning', truely: 'truly', arguement: 'argument', comming: 'coming', writting: 'writing',
    usefull: 'useful', adress: 'address', advertisment: 'advertisement', knowlege: 'knowledge',
    developement: 'development', independant: 'independent', responsability: 'responsibility',
    posible: 'possible', oportunity: 'opportunity', oppurtunity: 'opportunity',
    wether: 'whether', whith: 'with', becouse: 'because', thats: 'that is', dont: 'do not', doesnt: 'does not',
    didnt: 'did not', cant: 'cannot', isnt: 'is not', arent: 'are not', wont: 'will not', im: 'I am', ive: 'I have',
    youre: 'you are', theyre: 'they are',
    familys: 'families', childs: 'children', peoples: 'people', informations: 'information', advices: 'advice',
    knowledges: 'knowledge', equipments: 'equipment', researchs: 'research', homeworks: 'homework',
    evidences: 'evidence', furnitures: 'furniture', staffs: 'staff', moneys: 'money', feedbacks: 'feedback'
});
/* Valid words the frequency list (built from film subtitles) does not have —
   the vocabulary of IELTS topics, so a strong answer is not told it misspelt
   «telecommuting». Grows from what real essays flag. */
var ALLOW = bag();
('telecommuting telework teleworking ecommerce e-commerce cyberbullying desertification policymakers policymaker '
 + 'pedagogical respondents respondent stakeholders stakeholder socioeconomic wellbeing well-being lifelong extracurricular '
 + 'curricula overconsumption overpopulation underfunded underfunding microplastics deforestation reforestation afforestation '
 + 'biodiversity urbanisation urbanization globalisation globalization digitalisation digitalization automation '
 + 'gig homeschooling homeschooled e-learning online offline smartphone smartphones internet broadband '
 + 'carbon-neutral emissions decarbonisation decarbonization sustainability renewables geothermal hydroelectric '
 + 'photovoltaic agribusiness agricultural infrastructure telemedicine vaccination vaccinations obesity sedentary '
 + 'nutritional nutritious unhealthy recyclable recycling biodegradable landfill landfills incentivise incentivize '
 + 'subsidise subsidize subsidised subsidized privatisation privatization prioritise prioritize prioritised '
 + 'prioritized minimise minimize maximise maximize criminalise criminalize commodification gentrification '
 + 'multicultural multiculturalism intergenerational demographic demographics workforce workplace workplaces '
 + 'workaholic upskilling reskilling outsourcing offshoring entrepreneurial entrepreneurship startup startups '
 + 'cybercrime cybersecurity misinformation disinformation livestreaming streaming influencer influencers '
 + 'esports e-sports wellbeing whilst amongst learnt spelt dreamt burnt').split(' ').forEach(function (w) { ALLOW[w] = 1; });

/* ── markers ─────────────────────────────────────────────────────────────── */
var OVERVIEW_RE = /\b(overall|in general|generally speaking|on the whole|in summary|to summari[sz]e|in brief|at first glance|it is (clear|evident|apparent|noticeable|obvious|striking)|it can be (clearly )?seen|as (can be|is) (seen|shown)|a (glance|look) at|the most (striking|noticeable|significant|notable) (feature|trend|change|point))\b/i;
/* «I think» — the writer's own opinion. In Academic Task 1 it is a fault in itself. */
var OPINION_RE = /\b(i(?:\s+\w+ly)?\s+(?:believe|think|agree|disagree|feel|argue|contend|maintain|consider|support|oppose|reckon|assert|would argue|would say|am convinced|am of the (?:opinion|view)|tend to (?:agree|disagree|believe|think|feel))|in my (?:opinion|view|estimation|eyes)|from my (?:perspective|point of view|standpoint)|as far as i(?:'m|’m| am) concerned|my (?:own |personal |strong |firm )?(?:view|opinion|belief|position|stance) is|personally|it is my (?:belief|view|opinion))\b/i;
/* A position in Task 2 can also be stated without «I»: «This essay disagrees…»,
   «this is a negative development», «the benefits outweigh…». Measured on the
   corpus: the first-person list alone missed it in one strong essay in ten. */
var POSITION_RE = new RegExp(OPINION_RE.source + '|\\b(this essay (?:will )?(?:argues?|agrees?|disagrees?|contends?|maintains?|supports?)|i (?:am )?(?:in favou?r of|against)|i (?:would )?side with|(?:is|be|was|are) (?:a |an )?(?:\\w+ly )?(?:positive|negative|beneficial|detrimental|harmful|welcome|worrying|desirable|undesirable) (?:development|trend|change|phenomenon|step|thing)|(?:advantages|benefits|drawbacks|disadvantages|merits|pros|cons|positives|negatives) (?:\\w+ly )?(?:outweigh|outnumber))\\b', 'i');
var CONCLUSION_RE = /\b(in conclusion|to conclude|to sum up|in summary|to summari[sz]e|overall|all in all|in short|on balance|in a nutshell|conclusively|in brief|to wrap up|summing up|taking everything into account|having considered|considering all|all things considered|on the whole|by and large|in the final analysis|ultimately|as such|in the end)\b/i;
var PURPOSE_RE = /\b(i am writing|i'm writing|i write|i would like to (inform|complain|apply|request|ask|enquire|inquire|thank|invite|express|let you know)|this letter is|the purpose of this letter|i wanted to (let you know|tell you|thank|ask|invite))\b/i;
var GREET_RE = /^\s*(dear|hi|hello|to whom it may concern)\b[^\n]*/i;
var SIGNOFF_RE = /^\s*(yours faithfully|yours sincerely|yours truly|sincerely( yours)?|faithfully|best wishes|best regards|kind regards|warm regards|warmest regards|regards|many thanks|thanks again|all the best|love|lots of love|cheers|take care|see you soon|with best wishes)\b[^\n]*$/i;
var SUBORD_RE = /\b(because|although|though|whereas|while|which|who|whom|whose|that|if|unless|when|whenever|since|so that|in order to|provided that|as long as|even if|despite the fact)\b/gi;

function questionKind(q) {
    q = String(q || '').toLowerCase();
    return {
        opinion: /agree or disagree|to what extent|do you (agree|think|believe)|your (own )?(opinion|view)|what is your (view|opinion)|positive or negative development|outweigh/.test(q),
        discuss: /discuss both/.test(q),
        twoPart: (q.match(/\?/g) || []).length >= 2,
        dataChart: /(chart|graph|table|percentage|proportion|figures|data)/.test(q) && !/(process|diagram shows how|map|plan|life cycle|stages)/.test(q)
    };
}

/* ── spelling (Norvig: the most frequent known word within 1–2 edits) ───── */
var ALPHA = 'abcdefghijklmnopqrstuvwxyz';
function edits1(w) {
    var out = [], i, c;
    for (i = 0; i <= w.length; i++) {
        var a = w.slice(0, i), b = w.slice(i);
        if (b) out.push(a + b.slice(1));                                      // delete
        if (b.length > 1) out.push(a + b[1] + b[0] + b.slice(2));             // transpose
        for (c = 0; c < 26; c++) {
            if (b) out.push(a + ALPHA[c] + b.slice(1));                       // replace
            out.push(a + ALPHA[c] + b);                                       // insert
        }
    }
    return out;
}
/* Longest common subsequence — how much of what was typed survives in a
   candidate. Frequency alone picked «about» for «amout»: one edit away and ten
   times more common than «amount». Omitting a letter is the commonest slip, and
   the candidate that keeps every typed letter is usually the intended word. */
function lcs(a, b) {
    var prev = new Array(b.length + 1).fill(0), cur, i, j;
    for (i = 1; i <= a.length; i++) {
        cur = [0];
        for (j = 1; j <= b.length; j++) cur[j] = a[i - 1] === b[j - 1] ? prev[j - 1] + 1 : Math.max(prev[j], cur[j - 1]);
        prev = cur;
    }
    return prev[b.length];
}
var RUN_TOGETHER = bag();
['at least', 'in front', 'each other', 'as well', 'in spite', 'every time', 'no one', 'any more', 'in fact', 'of course',
 'even though', 'even if', 'as soon', 'in order', 'at all', 'so far', 'such as', 'in case', 'at once', 'in addition',
 'on time', 'up to', 'as long', 'thank you', 'high school', 'on top', 'per cent', 'a lot', 'in particular', 'in general',
 'for example', 'for instance', 'on the', 'in the', 'of the'].forEach(function (p) { RUN_TOGETHER[p] = 1; });
var _sugCache = bag();
/* Up to three candidates, best first: [{w, d}] with d = edit distance (1|2),
   or a split «atleast» → «at least». Empty — nothing plausible is close, which
   for an unknown word usually means a rare real word, not a typo. */
function suggestAll(word, dict) {
    if (word in _sugCache) return _sugCache[word];
    var seen = bag(), cands = [], e1 = edits1(word), i, j;
    function consider(w, d) {
        if (seen[w] || w === word || !dict.known(w)) return;
        var r = dict.rank(w) || 1e9;
        if (d === 2 && r > 20000) return;      // two edits away AND rare: more often a different real word
        seen[w] = 1;
        cands.push({ w: w, d: d, r: r, l: lcs(word, w) });
    }
    for (i = 0; i < e1.length; i++) consider(e1[i], 1);
    if (!cands.length && word.length <= 12) {
        for (i = 0; i < e1.length; i++) { var e2 = edits1(e1[i]); for (j = 0; j < e2.length; j++) consider(e2[j], 2); }
    }
    // «atleast», «infront», «eachother», «aswell» — a fixed phrase run together.
    // Only the known phrases: any two common words split «outweight» into «out
    // weight» and buried the intended «outweigh».
    for (i = 2; i <= word.length - 2; i++) {
        var two = word.slice(0, i) + ' ' + word.slice(i);
        if (RUN_TOGETHER[two]) { cands.push({ w: two, d: 1, r: 1, l: word.length, split: 1 }); break; }
    }
    // Order: a run-together phrase, then one word that keeps every typed letter
    // (a missed letter — «amout» → amount), then the rest by how much of the
    // typing they keep, then by frequency.
    function kind(c) { return c.split ? 0 : (c.l === word.length ? 1 : 2); }
    cands.sort(function (x, y) { return (x.d - y.d) || (kind(x) - kind(y)) || (y.l - x.l) || (x.r - y.r); });
    var out = cands.slice(0, 3).map(function (c) { return { w: c.w, d: c.d, r: c.r }; });
    _sugCache[word] = out;
    return out;
}
function suggest(word, dict) { var s = suggestAll(word, dict); return s.length ? s[0].w : null; }

/* A word the subtitle-built list lacks may still be a regular derivation of
   one it has: «unconsidered», «workless», «totalised». Accept those instead of
   flagging them — a check that cries wolf on correct words is worse than none. */
/* ⚠️ No «out»: out + weight made «outweight» (for outweigh) a real word. */
var PREFIXES = ['un', 'in', 'im', 'il', 'ir', 'dis', 'non', 'over', 'under', 're', 'pre', 'co', 'multi', 'inter', 'anti', 'self', 'mis', 'sub', 'super', 'post', 'semi', 'micro', 'eco', 'cyber', 'tele', 'bio'];
var SUFFIXES = ['less', 'ness', 'ity', 'ism', 'ist', 'ists', 'ise', 'ize', 'ised', 'ized', 'ises', 'izes', 'ising', 'izing', 'isation', 'ization', 'ment', 'ments', 'al', 'ally', 'able', 'ible', 'ful', 'wards', 'ward', 'wise', 'ship', 'hood', 'like', 'free', 'based'];
function isKnown(lc, dict) {
    if (ALLOW[lc] || dict.known(lc)) return true;
    var lm = dict.lemmatize ? dict.lemmatize(lc) : lc;
    if (lm && lm !== lc && (dict.known(lm) || ALLOW[lm])) return true;
    var i, stem;
    for (i = 0; i < PREFIXES.length; i++) {
        if (lc.length - PREFIXES[i].length >= 4 && lc.indexOf(PREFIXES[i]) === 0) {
            stem = lc.slice(PREFIXES[i].length);
            if (dict.known(stem) || ALLOW[stem]) return true;
            var lm2 = dict.lemmatize ? dict.lemmatize(stem) : stem;
            if (lm2 && lm2 !== stem && dict.known(lm2)) return true;
        }
    }
    for (i = 0; i < SUFFIXES.length; i++) {
        var sf = SUFFIXES[i];
        if (lc.length - sf.length >= 3 && lc.slice(-sf.length) === sf) {
            stem = lc.slice(0, -sf.length);
            if (dict.known(stem) || dict.known(stem + 'e') || dict.known(stem + 'y') || ALLOW[stem]) return true;
        }
    }
    return false;
}

/* ── moving-average type/token ratio ─────────────────────────────────────
   Plain unique/total falls as an answer gets longer, so it told a 380-word
   essay its vocabulary was «narrow» and a 160-word one it was «rich». MATTR
   over a 50-word window does not depend on length. */
function mattr(lcWords, win) {
    win = win || 50;
    if (lcWords.length < win) return lcWords.length ? (new Set(lcWords)).size / lcWords.length : 0;
    var counts = {}, uniq = 0, sum = 0, i;
    for (i = 0; i < win; i++) { if (!counts[lcWords[i]]) uniq++; counts[lcWords[i]] = (counts[lcWords[i]] || 0) + 1; }
    sum += uniq / win;
    for (i = win; i < lcWords.length; i++) {
        var out = lcWords[i - win];
        if (--counts[out] === 0) uniq--;
        if (!counts[lcWords[i]]) uniq++;
        counts[lcWords[i]] = (counts[lcWords[i]] || 0) + 1;
        sum += uniq / win;
    }
    return sum / (lcWords.length - win + 1);
}

function crudeLemma(w) {
    if (w.length > 4 && /ies$/.test(w)) return w.slice(0, -3) + 'y';
    if (w.length > 4 && /s$/.test(w) && !/(ss|us|is|ous)$/.test(w)) return w.slice(0, -1);
    return w;
}

/* ── the analysis ────────────────────────────────────────────────────────── */
function analyze(text, opts) {
    opts = opts || {};
    text = String(text || '');
    var task = opts.task || 'task2', fam = family(task), q = String(opts.question || '');
    var dict = (opts.dict && typeof opts.dict.known === 'function') ? opts.dict : null;
    var minWords = MIN_WORDS[fam];
    var words = wordCount(text);
    var toks = tokens(text);
    var paras = paragraphs(text);
    var sents = sentences(text);
    var findings = [];
    function add(id, crit, sev, ranges, params, fixes) {
        findings.push({ id: id, crit: crit, sev: sev, ranges: ranges || [], params: params || {}, fixes: fixes || null });
    }

    /* GT letter frame: greeting and sign-off lines are not paragraphs of content. */
    var isLetter = task === 'task1_gt';
    var greet = null, signoff = null, bodyParas = paras;
    if (isLetter && paras.length) {
        if (GREET_RE.test(paras[0].text) && paras[0].text.length < 70) greet = paras[0];
        for (var pi = paras.length - 1; pi >= Math.max(0, paras.length - 3); pi--) {
            if (SIGNOFF_RE.test(paras[pi].text) && paras[pi].text.length < 45) { signoff = paras[pi]; break; }
        }
        bodyParas = paras.filter(function (p) {
            if (p === greet) return false;
            if (signoff && p.s >= signoff.s) return false;   // the sign-off and the name under it
            return true;
        });
    }
    var nParas = bodyParas.length;

    /* sentence stats */
    var sentWords = sents.map(function (s) { return wordCount(s.text); });
    var nSent = sents.length;
    var avgSent = nSent ? Math.round((sentWords.reduce(function (a, b) { return a + b; }, 0) / nSent) * 10) / 10 : 0;
    var subord = (text.match(SUBORD_RE) || []).length;

    var empty = words === 0;
    if (!empty && words <= 20) add('too_short', 'task', 3, [], { n: words });

    /* ── task ── */
    if (words > 20 && words < minWords) add('under_length', 'task', 3, [], { n: words, min: minWords });

    var qk = questionKind(q);
    if (fam === 'task2' && words >= 120 && qk.opinion && !POSITION_RE.test(text)) add('no_position', 'task', 2, []);
    // Only a four-paragraph essay has an unmistakable last paragraph; in a
    // three-paragraph one the last block is as often a body paragraph.
    if (fam === 'task2' && nParas >= 4) {
        var last = bodyParas[nParas - 1];
        if (!CONCLUSION_RE.test(last.text)) add('no_conclusion', 'task', 1, [[last.s, Math.min(last.e, last.s + 60)]]);
    }
    if (task === 'task1_academic' && words >= 60) {
        if (!OVERVIEW_RE.test(text)) add('no_overview', 'task', 3, []);
        var om = text.match(OPINION_RE);
        if (om) { var oi = text.search(OPINION_RE); add('opinion_in_t1', 'task', 2, [[oi, oi + om[0].length]]); }
        if (qk.dataChart && !/\d|\b(half|quarter|third|double|twice|treble|triple|percent)\b/i.test(text)) add('no_figures', 'task', 2, []);
    }
    if (isLetter && words >= 40) {
        if (!greet) add('no_greeting', 'task', 2, []);
        if (!signoff) add('no_signoff', 'task', 2, []);
        if (greet && signoff) {
            var g = greet.text.toLowerCase(), so = signoff.text.toLowerCase();
            var formal = /dear (sir|madam|sir or madam|sir\/madam|sirs|manager|hiring|editor|customer|team)\b|to whom it may concern/.test(g);
            var semi = !formal && /dear (mr|mrs|ms|miss|dr|prof)\.?\s/.test(g);
            var informal = !formal && !semi;
            if (formal && /sincerely/.test(so)) add('signoff_mismatch', 'task', 2, [[signoff.s, signoff.e]], { want: 'Yours faithfully' });
            else if (semi && /faithfully/.test(so)) add('signoff_mismatch', 'task', 2, [[signoff.s, signoff.e]], { want: 'Yours sincerely' });
            else if (informal && /faithfully|sincerely|truly/.test(so)) add('signoff_mismatch', 'task', 1, [[signoff.s, signoff.e]], { want: 'Best wishes' });
            var asked = q.match(/begin your letter as follows:?\s*\n?\s*(dear [^\n,.]+)/i);
            if (asked && g.indexOf(asked[1].toLowerCase().replace(/\s+/g, ' ').trim()) !== 0) add('greeting_mismatch', 'task', 2, [[greet.s, greet.e]], { want: asked[1].trim() });
            if (!informal && bodyParas.length && !PURPOSE_RE.test(bodyParas[0].text)) add('no_purpose', 'task', 1, [[bodyParas[0].s, Math.min(bodyParas[0].e, bodyParas[0].s + 60)]]);
        }
        /* The three bullet points of a GT prompt: a bullet with no word of its
           own in the letter has most likely been missed — and a missed bullet
           caps Task Achievement. Soft wording on the page: «check it». */
        var bullets = [];
        q.split('\n').forEach(function (line) {
            var mm = line.match(/^\s*(?:[•●▪◦\-*–]|\d+[.)])\s*(.+)$/);
            if (mm) bullets.push(mm[1].trim());
        });
        if (bullets.length >= 2 && bullets.length <= 5) {
            var have = bag();
            toks.forEach(function (t) { have[crudeLemma(t.lc)] = 1; have[t.lc] = 1; });
            bullets.forEach(function (b, bi) {
                var kw = tokens(b).map(function (t) { return t.lc; })
                    .filter(function (w) { return w.length >= 4 && !STOP[w] && !PROMPT_BOILER[w] && !/^(explain|describe|say|tell|suggest|give|ask|what|which|would|like|your|about|when|where|whether)$/.test(w); });
                if (kw.length < 2) return;
                var hit = kw.some(function (w) { return have[w] || have[crudeLemma(w)]; });
                if (!hit) add('bullet_missed', 'task', 2, [], { n: bi + 1, text: b.length > 70 ? b.slice(0, 67) + '…' : b });
            });
        }
    }

    /* Off-topic: how much of the question's own subject vocabulary turns up. */
    var qWords = bag();
    tokens(q).forEach(function (t) { var w = t.lc; if (w.length >= 4 && !STOP[w] && !PROMPT_BOILER[w]) qWords[crudeLemma(w)] = 1; });
    var qKeys = Object.keys(qWords), overlap = null;
    if (qKeys.length >= 4 && words >= 80) {
        var essaySet = bag();
        toks.forEach(function (t) { essaySet[crudeLemma(t.lc)] = 1; });
        var hitN = qKeys.filter(function (w) { return essaySet[w]; }).length;
        overlap = Math.round((hitN / qKeys.length) * 100) / 100;
        if (overlap < 0.15) add('off_topic', 'task', 3, [], { pct: Math.round(overlap * 100) });
    }

    /* ── coherence & cohesion ── */
    if (words > 80 && nParas <= 1) add('single_block', 'coherence', 3, []);
    else if (fam === 'task2' && words >= 180 && nParas >= 2 && nParas <= 3) add('few_paragraphs', 'coherence', 1, [], { n: nParas });
    var bulletLines = [];
    if (!isLetter) {
        paras.forEach(function (p) { if (/^\s*(?:[•●▪◦\-*–]|\d+[.)])\s+/.test(p.text)) bulletLines.push([p.s, Math.min(p.e, p.s + 40)]); });
        if (bulletLines.length >= 2) add('bullet_list', 'coherence', 2, bulletLines);
    }
    var links = findLinkers(text);
    var byLink = bag();
    links.forEach(function (h) { (byLink[h.p] = byLink[h.p] || []).push(h); });
    var overused = [];
    for (var lp in byLink) if (SIGNPOSTS[lp] && byLink[lp].length >= 3) overused.push(lp);
    overused.forEach(function (lp) {
        add('linker_overuse', 'coherence', 2, byLink[lp].map(function (h) { return [h.s, h.e]; }), { w: lp, n: byLink[lp].length });
    });
    // Sentence after sentence opening on a signpost reads mechanical even when
    // no single one repeats (Band 6: «cohesion … may be faulty or mechanical»).
    if (nSent >= 8) {
        var openers = sents.filter(function (s) {
            var head = s.text.slice(0, 40).toLowerCase();
            for (var k in SIGNPOSTS) if (head.indexOf(k) === 0) return true;
            return false;
        });
        if (openers.length / nSent >= 0.5 && !overused.length) {
            add('linker_mechanical', 'coherence', 2, openers.map(function (s) { return [s.s, s.s + Math.min(18, s.text.length)]; }), { n: openers.length, of: nSent });
        }
    }
    var distinct = Object.keys(byLink).length;
    if (fam === 'task2' && words >= 150 && distinct < 3) add('linker_few', 'coherence', 1, [], { n: distinct });

    /* ── lexical resource ── */
    var content = toks.filter(function (t) { return t.lc.length >= 4 && !STOP[t.lc] && !/['’]/.test(t.lc); });
    var counts = bag(), spans = bag();
    content.forEach(function (t) { var k = crudeLemma(t.lc); counts[k] = (counts[k] || 0) + 1; (spans[k] = spans[k] || []).push([t.s, t.e]); });
    /* Repetition. Measured on the corpus: a «4 times» rule fired on 93 % of
       essays at EVERY band — the words were the subject itself (people,
       country, water, percentage), which a band-9 answer repeats too. So the
       frequent words are shown as information, and only a word that keeps
       coming back beyond the topic is a finding. */
    var topWords = [], reps = [], cw;
    for (cw in counts) if (counts[cw] >= 3) topWords.push({ w: cw, n: counts[cw], topic: !!qWords[cw] });
    topWords.sort(function (a, b) { return b.n - a.n; });
    var haveQ = qKeys.length > 0;
    // Task 1 describes a chart, a process or a letter's business: its nouns ARE
    // the data («water», «million», «stage», «room») and recur by design.
    if (fam === 'task2') for (cw in counts) {
        var share = counts[cw] / Math.max(words, 1);
        if (!qWords[cw] && counts[cw] >= (haveQ ? 6 : 7) && share >= (haveQ ? 0.02 : 0.03)) reps.push({ w: cw, n: counts[cw] });
        else if (qWords[cw] && counts[cw] >= 9 && share >= 0.04) reps.push({ w: cw, n: counts[cw] });
    }
    reps.sort(function (a, b) { return b.n - a.n; });
    reps.slice(0, 3).forEach(function (r) { add('repetition', 'lexical', 1, spans[r.w], { w: r.w, n: r.n, topic: !!qWords[r.w] }); });

    var formalAnswer = !(isLetter && greet && !/dear (sir|madam|mr|mrs|ms|miss|dr|prof)|to whom/i.test(greet.text));
    if (formalAnswer) {
        var cHits = [], cFix = [];
        toks.forEach(function (t) {
            var k = t.lc.replace(/’/g, "'");
            if (CONTRACTIONS[k]) {
                var to = CONTRACTIONS[k];
                if (k === "it's" && /^it['’]s\s+(been|got)\b/i.test(text.slice(t.s, t.s + 12))) to = 'it has';
                if (t.w[0] === t.w[0].toUpperCase()) to = to[0].toUpperCase() + to.slice(1);
                cHits.push([t.s, t.e]); cFix.push({ s: t.s, e: t.e, to: to });
            }
        });
        if (cHits.length) add('contractions', 'lexical', 1, cHits, { n: cHits.length, ex: text.slice(cHits[0][0], cHits[0][1]) }, cFix);
        INFORMAL.forEach(function (it) {
            var re = phraseRe(it.p), m, rs = [], fx = [];
            while ((m = re.exec(text))) {
                rs.push([m.index, m.index + m[0].length]);
                if (it.to) fx.push({ s: m.index, e: m.index + m[0].length, to: (m[0][0] === m[0][0].toUpperCase() ? it.to[0].toUpperCase() + it.to.slice(1) : it.to) });
            }
            if (rs.length) add('informal', 'lexical', 1, rs, { w: it.p, alt: it.alt || it.to, n: rs.length }, fx.length ? fx : null);
        });
        var youN = toks.filter(function (t) { return t.lc === 'you' || t.lc === 'your'; });
        if (fam === 'task2' && youN.length >= 3) add('you_reader', 'lexical', 1, youN.map(function (t) { return [t.s, t.e]; }), { n: youN.length });
    }
    CLICHES.forEach(function (c) {
        var re = phraseRe(c), m, rs = [];
        while ((m = re.exec(text))) rs.push([m.index, m.index + m[0].length]);
        if (rs.length) add('cliche', 'lexical', 1, rs, { w: c });
    });
    for (var vw in VAGUE) {
        var vt = toks.filter(function (t) { return t.lc === vw; });
        if (vt.length >= (vw === 'get' || vw === 'got' ? 4 : 3)) add('vague', 'lexical', 1, vt.map(function (t) { return [t.s, t.e]; }), { w: vw, n: vt.length, alt: VAGUE[vw] });
    }

    var lcAll = toks.map(function (t) { return t.lc; });
    var diversity = Math.round(mattr(lcAll, 50) * 100) / 100;
    if (lcAll.length >= 120 && diversity < 0.6) add('low_diversity', 'lexical', 1, [], { v: diversity });

    /* spelling — only with the dictionary */
    var typos = [];
    if (dict) {
        var sentStarts = bag();
        sents.forEach(function (s) { sentStarts[s.s] = 1; });
        toks.forEach(function (t) {
            var parts = t.w.split('-'), off = t.s;
            parts.forEach(function (part) {
                var lc = part.toLowerCase().replace(/’/g, "'"), s0 = off;
                off += part.length + 1;
                if (lc.length < 3 || /['’]/.test(lc)) return;
                var caps = part[0] !== part[0].toLowerCase();
                if (caps && !sentStarts[s0]) return;                       // a name, a place, an acronym
                if (part === part.toUpperCase() && part.length > 1) return;
                if (opts.ignore && opts.ignore[lc]) return;                // «not a typo» — the writer said so
                var known = MISSPELT[lc] ? null : isKnown(lc, dict);
                if (known) return;
                var alts;
                if (MISSPELT[lc]) alts = [MISSPELT[lc]];
                else {
                    var sg = suggestAll(lc, dict);
                    // A capitalised word at the start of a sentence may be a
                    // name (Samsung, Nokia): only a one-letter slip of a common
                    // word counts there («Fisrt», «Therfore», «Orveall»).
                    if (caps) sg = sg.filter(function (c) { return c.d === 1 && c.r <= 5000; });
                    alts = sg.map(function (c) { return c.w; });
                }
                if (!alts.length) return;                                   // nothing close: a rare real word, not a typo
                if (caps) alts = alts.map(function (a) { return a[0].toUpperCase() + a.slice(1); });
                typos.push({ s: s0, e: s0 + part.length, w: part, to: alts[0], alts: alts });
            });
        });
        if (typos.length) {
            add('typos', 'lexical', typos.length >= 4 ? 2 : 1, typos.map(function (t) { return [t.s, t.e]; }),
                { n: typos.length, ex: typos.slice(0, 3).map(function (t) { return t.w + ' → ' + t.to; }).join(', ') },
                typos.map(function (t) { return { s: t.s, e: t.e, to: t.to, alts: t.alts, w: t.w }; }));
        }
    }

    /* vocabulary profile — only with the CEFR list */
    var vocab = null;
    if (dict && typeof dict.cefrBand === 'function' && content.length >= 30) {
        var bands = { A1: 0, A2: 0, B1: 0, B2: 0, C1: 0, C2: 0 }, strong = bag(), nC = 0;
        var starts = bag();
        sents.forEach(function (x) { starts[x.s] = 1; });
        content.forEach(function (t) {
            // A capital mid-sentence is a name (Slack, Zoom) — not vocabulary.
            if (t.w[0] !== t.w[0].toLowerCase() && !starts[t.s]) return;
            var b = dict.cefrBand(t.lc);
            if (!b || !bands.hasOwnProperty(b)) return;
            bands[b]++; nC++;
            if ((b === 'C1' || b === 'C2') && isKnown(t.lc, dict)) strong[t.lc] = 1;
        });
        if (nC) {
            vocab = {
                n: nC,
                pct: {
                    A: Math.round((bands.A1 + bands.A2) / nC * 100), B1: Math.round(bands.B1 / nC * 100),
                    B2: Math.round(bands.B2 / nC * 100), C: Math.round((bands.C1 + bands.C2) / nC * 100)
                },
                strong: Object.keys(strong).slice(0, 8)
            };
        }
    }

    /* ── grammatical range & accuracy (the mechanical part of it) ── */
    /* A long sentence is not a fault — strong essays have MORE sentences over 40
       words than weak ones (corpus: 43 % vs 36 %). Past ~60 words it is almost
       always a run-on held together by commas, and that is where accuracy goes. */
    var longOnes = [];
    sents.forEach(function (s, i) { if (sentWords[i] > 60) longOnes.push([s.s, s.e]); });
    if (longOnes.length) add('long_sentence', 'grammar', 1, longOnes, { n: longOnes.length, max: Math.max.apply(null, sentWords) });
    if (nSent >= 6 && avgSent > 0 && avgSent < 11 && subord / nSent < 0.6) add('simple_sentences', 'grammar', 2, [], { avg: avgSent });
    var lowerI = [], lre = /(^|[\s(“"])i(?=[\s,.;:!?)”"']|$)/g, lm;
    while ((lm = lre.exec(text))) {
        var at = lm.index + lm[1].length;
        if (/^i\.e/i.test(text.slice(at, at + 3))) continue;                    // «i.e.»
        if (lm[1] === '(' && text[at + 1] === ')') continue;                    // «(i)» — a list numeral
        lowerI.push([at, at + 1]);
    }
    if (lowerI.length) add('lowercase_i', 'grammar', 1, lowerI, { n: lowerI.length }, lowerI.map(function (r) { return { s: r[0], e: r[1], to: 'I' }; }));
    var lowStarts = [];
    sents.forEach(function (s) { if (/^[a-z]/.test(s.text) && !/^(e\.g|i\.e|etc)\b/.test(s.text)) lowStarts.push([s.s, s.s + 1]); });
    if (lowStarts.length) add('lowercase_start', 'grammar', 1, lowStarts, { n: lowStarts.length },
        lowStarts.map(function (r) { return { s: r[0], e: r[1], to: text[r[0]].toUpperCase() }; }));
    var spacing = [], spre = /[A-Za-z][,;:](?=[A-Za-z])|[A-Za-z]\s+[,;:](?=\s|$)/g, sm;
    while ((spacing.length < 30) && (sm = spre.exec(text))) spacing.push([sm.index + 1, sm.index + sm[0].length]);
    if (spacing.length) add('spacing', 'grammar', 1, spacing, { n: spacing.length });

    /* linkers summary for the page («what you used») */
    var linkerSummary = Object.keys(byLink).map(function (p) { return { p: p, n: byLink[p].length, k: byLink[p][0].k }; })
        .sort(function (a, b) { return b.n - a.n; });

    findings.sort(function (a, b) { return b.sev - a.sev; });
    return {
        task: task, family: fam, words: words, minWords: minWords, chars: text.length,
        paragraphs: nParas, sentences: nSent, avgSentence: avgSent, longest: sentWords.length ? Math.max.apply(null, sentWords) : 0,
        diversity: diversity, linkers: linkerSummary, linkerCount: links.length, vocab: vocab, overlap: overlap,
        topWords: topWords.slice(0, 6),
        findings: findings, letter: isLetter ? { greeting: greet ? greet.text : '', signoff: signoff ? signoff.text : '' } : null
    };
}

/* A finding's message: «:name» → value, «{words}» → the form of «words» that
   agrees with :n, «{words:max}» → with :max. Plural tokens go FIRST — the
   placeholder pass would otherwise eat the «:max» inside «{words:max}» and
   print «{words45}» (found in review 2026-10-01, every locale).
   `forms` is {words: {one, few, many, other}, …} keyed by Intl.PluralRules. */
var _rules = {};
function plural(n, forms, locale) {
    if (!forms) return '';
    var r = _rules[locale];
    if (!r) {
        try { r = new Intl.PluralRules(locale || 'en'); } catch (e) { r = { select: function (x) { return x === 1 ? 'one' : 'other'; } }; }
        _rules[locale] = r;
    }
    var c = r.select(Math.abs(Number(n) || 0));
    return forms[c] || forms.other || forms.many || forms.one || '';
}
function format(template, params, forms, locale) {
    var p = params || {}, pl = forms || {};
    var out = String(template == null ? '' : template).replace(/\{(words|times|paras)(?::([a-z_]+))?\}/g, function (m, k, by) {
        return plural(p[by || 'n'] != null ? p[by || 'n'] : 0, pl[k], locale);
    });
    return out.replace(/:([a-z_]+)/gi, function (m, k) { return Object.prototype.hasOwnProperty.call(p, k) ? String(p[k]) : m; });
}

/* ═══ an examiner's fix, as a change to the candidate's own words ════════
   The grader returns each fix in one of two shapes: the corrected sentence
   itself (the RU grader: «Programs like Zoom and Slack allow employees …»)
   or a reason with the correction quoted (the intl grader: «Correct the
   spelling: 'separate' (not 'seperate')»). Shown as «quote → fix», both make
   the candidate compare two long sentences to find one letter. correction()
   turns either shape into the quote with the change applied, so the page can
   mark exactly the words that changed; advice that is not a correction of
   these words («use 'In addition' for the second point») returns null and
   stays as advice. */
var Q_PAIRS = { "'": "'\u2019", '"': '"\u201D', '\u2018': '\u2019\'', '\u201C': '\u201D"', '\u201E': '\u201C\u201D', '\u00AB': '\u00BB' };
var FIX_WORD = /[A-Za-z0-9\u00C0-\u024F]+(?:['\u2019][A-Za-z]+)*/g;
var FIX_SPACE = /[\s\u00A0\u202F]/;

/* Quoted parts of a fix text. An opening mark starts the text or follows a
   space, bracket, colon or dash; a closing mark follows a non-space and is
   followed by the end, a space or punctuation — so the apostrophe inside
   «doesn't» is neither. French « … » may carry inner spaces. */
function quotedParts(s) {
    var out = [], i = 0, n = s.length;
    while (i < n) {
        var c = s.charAt(i), close = Q_PAIRS[c];
        if (close && (i === 0 || /[\s\u00A0(\[:\u2014\u2013-]/.test(s.charAt(i - 1)))) {
            var a = i + 1;
            if (c === '\u00AB') while (a < n && FIX_SPACE.test(s.charAt(a))) a++;
            if (a < n && !FIX_SPACE.test(s.charAt(a))) {
                var j = a;
                for (; j < n; j++) {
                    if (close.indexOf(s.charAt(j)) < 0) continue;
                    if ((c === '\u00AB' || !FIX_SPACE.test(s.charAt(j - 1))) && (j + 1 === n || /[\s\u00A0.,;:!?)\]]/.test(s.charAt(j + 1)))) break;
                }
                var inner = j < n ? s.slice(a, j).replace(/[\s\u00A0\u202F]+$/, '') : '';
                if (inner) { out.push({ s: i, e: j + 1, text: inner }); i = j + 1; continue; }
            }
        }
        i++;
    }
    return out;
}
function fixWords(s) {
    var out = [], m, re = new RegExp(FIX_WORD.source, 'g');
    while ((m = re.exec(s))) out.push({ s: m.index, e: m.index + m[0].length, w: m[0].toLowerCase().replace(/\u2019/g, "'") });
    return out;
}
/* Levenshtein distance, enough to tell a respelling from another word. */
function editDistance(a, b) {
    var prev = [], cur, i, j;
    for (j = 0; j <= b.length; j++) prev[j] = j;
    for (i = 1; i <= a.length; i++) {
        cur = [i];
        for (j = 1; j <= b.length; j++) cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1));
        prev = cur;
    }
    return prev[b.length];
}
/* Where `w` stands in `s` as whole words («outweigh» is not inside «outweight»). */
function wholeAt(s, w) {
    var out = [], k = -1;
    if (!w) return out;
    while ((k = s.indexOf(w, k + 1)) >= 0) {
        if (!/[A-Za-z0-9]/.test(k ? s.charAt(k - 1) : '') && !/[A-Za-z0-9]/.test(s.charAt(k + w.length))) out.push(k);
    }
    return out;
}
/* Put corrected text `g` in place of the stretch of the quote it corrects —
   the window of quote words most like it. Null when no stretch is close
   enough to be the same words corrected: a one-word fix must be a near
   spelling (≤ 2 edits), a longer one must keep most of its words (≥ 60 %,
   at least three words). */
function placeInto(quote, g) {
    var qs = fixWords(quote), gs = fixWords(g), gw = gs.map(function (x) { return x.w; });
    if (!qs.length || !gw.length || (gw.length > 1 && gw.length < 3)) return null;
    var best = null;
    for (var L = Math.max(1, gw.length - 2); L <= Math.min(qs.length, gw.length + 2); L++) {
        for (var i = 0; i + L <= qs.length; i++) {
            var win = qs.slice(i, i + L).map(function (x) { return x.w; }), sim = 0;
            if (gw.length === 1) {
                if (L === 1 && win[0] !== gw[0]) {
                    var d = editDistance(win[0], gw[0]);
                    if (d <= 2) sim = 1 - d / Math.max(win[0].length, gw[0].length);
                }
            } else sim = lcs(win, gw) / Math.max(L, gw.length);
            if (sim >= 0.6 && (!best || sim > best.sim || (sim === best.sim && L > best.L))) best = { sim: sim, i: i, L: L };
        }
    }
    if (!best) return null;
    // the corrected words as written, first to last: the full stop that closes
    // the examiner's own sentence is not part of the correction
    return quote.slice(0, qs[best.i].s) + g.slice(gs[0].s, gs[gs.length - 1].e) + quote.slice(qs[best.i + best.L - 1].e);
}
/* «Correct the spelling: 'separate'» → «Correct the spelling»: the text before
   the first quoted part that follows a colon or dash. A fix that IS the
   corrected text has no reason; a fix of another shape is the reason whole. */
function reasonOf(fix, parts, wholeIsFix) {
    if (wholeIsFix) return '';
    for (var i = 0; i < parts.length; i++) {
        var head = fix.slice(0, parts[i].s).replace(/[\s\u00A0]+$/, '');
        if (!/[:\u2014\u2013-]$/.test(head)) continue;
        head = head.replace(/[\s\u00A0]*[:\u2014\u2013-]+$/, '');
        if ((head.match(/\S+/g) || []).length >= 2) return head;
    }
    return fix;
}
function correction(quote, fix) {
    quote = String(quote || ''); fix = String(fix || '').trim();
    if (!quote || !fix) return null;
    var parts = quotedParts(fix), text = null, whole = false;
    if (!parts.length) {
        text = placeInto(quote, fix);
        whole = text != null;
    } else {
        var inQ = parts.filter(function (p) { return wholeAt(quote, p.text).length === 1; });
        var outQ = parts.filter(function (p) { return !wholeAt(quote, p.text).length; });
        // «'separate' (not 'seperate')», «'reasons for' instead of 'reasons of'»: one part is
        // the quote's own words, the other replaces them — when both are short
        if (parts.length === 2 && inQ.length === 1 && outQ.length === 1
            && fixWords(inQ[0].text).length <= 6 && fixWords(outQ[0].text).length <= fixWords(inQ[0].text).length + 3) {
            var k = wholeAt(quote, inQ[0].text)[0];
            text = quote.slice(0, k) + outQ[0].text + quote.slice(k + inQ[0].text.length);
        }
        for (var i = 0; i < outQ.length && text == null; i++) text = placeInto(quote, outQ[i].text);
    }
    if (text == null || text === quote) return null;
    return { text: text, why: reasonOf(fix, parts, whole) };
}
/* The reason in the translated fix («Korrigiere die Rechtschreibung: „separate“»);
   a translation without quotes comes back whole. */
function correctionReason(fix) {
    fix = String(fix || '').trim();
    return fix ? reasonOf(fix, quotedParts(fix), false) : '';
}
/* Token diff a → b as [{op: '=' | '-' | '+', s}] over words, spaces and
   punctuation — null when the texts are too long to compare on a keystroke.
   Changes separated only by a space read as one change («there house» →
   «their home»; a paragraph break between them stays); a curly apostrophe in
   the essay and a straight one in the fix are the same letter. */
function diffTokens(s) { return String(s || '').match(/\s+|[A-Za-z0-9\u00C0-\u024F]+(?:['\u2019][A-Za-z]+)*|[^\sA-Za-z0-9\u00C0-\u024F]/g) || []; }
function sameMark(t) { return t.replace(/[\u2018\u2019\u02BC]/g, "'").replace(/[\u201C\u201D]/g, '"'); }
function wordDiff(a, b) {
    var A1 = diffTokens(a), B1 = diffTokens(b), n = A1.length, m = B1.length, i, j, ops = [];
    function push(op, s) { var l = ops[ops.length - 1]; if (l && l.op === op) l.s += s; else ops.push({ op: op, s: s }); }
    if (n * m > 4e6) return null;
    var a2 = A1.map(sameMark), b2 = B1.map(sameMark), dp = [];
    for (i = 0; i <= n; i++) dp[i] = new Uint16Array(m + 1);
    for (i = n - 1; i >= 0; i--) for (j = m - 1; j >= 0; j--) dp[i][j] = a2[i] === b2[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    i = 0; j = 0;
    while (i < n && j < m) {
        if (a2[i] === b2[j]) { push('=', A1[i]); i++; j++; }
        else if (dp[i + 1][j] >= dp[i][j + 1]) { push('-', A1[i]); i++; }
        else { push('+', B1[j]); j++; }
    }
    while (i < n) push('-', A1[i++]);
    while (j < m) push('+', B1[j++]);
    var out = [], k = 0;
    function pushOut(op, s) { var l = out[out.length - 1]; if (l && l.op === op) l.s += s; else out.push({ op: op, s: s }); }
    while (k < ops.length) {
        if (ops[k].op === '=') { pushOut('=', ops[k].s); k++; continue; }
        var ds = '', is = '', x = k, pre = '', post = '';
        for (; x < ops.length; x++) {
            var o = ops[x];
            if (o.op === '-') ds += o.s;
            else if (o.op === '+') is += o.s;
            else if (!/^[^\S\n]+$/.test(o.s) || x + 1 >= ops.length || ops[x + 1].op === '=') break;
            else { if (ds) ds += o.s; if (is) is += o.s; }   // the space between two changes is on both sides
        }
        // a space at the edge of BOTH sides of a change belongs to the text around it
        if (ds && is && /^\s/.test(ds) && /^\s/.test(is)) { pre = /^\s+/.exec(is)[0]; ds = ds.replace(/^\s+/, ''); is = is.replace(/^\s+/, ''); }
        if (ds && is && /\s$/.test(ds) && /\s$/.test(is)) { post = /\s+$/.exec(is)[0]; ds = ds.replace(/\s+$/, ''); is = is.replace(/\s+$/, ''); }
        if (pre) pushOut('=', pre);
        if (ds) out.push({ op: '-', s: ds });
        if (is) out.push({ op: '+', s: is });
        if (post) pushOut('=', post);
        k = x;
    }
    return out;
}

return {
    analyze: analyze,
    correction: correction,
    correctionReason: correctionReason,
    wordDiff: wordDiff,
    format: format,
    plural: plural,
    wordCount: wordCount,
    sentences: sentences,
    paragraphs: paragraphs,
    tokens: tokens,
    suggest: suggest,
    suggestAll: suggestAll,
    isKnown: isKnown,
    mattr: mattr,
    family: family,
    MIN_WORDS: MIN_WORDS,
    LINKERS: LINKERS
};
});
